import React from "react";
import DividerMotif from "../../components/Brand/DividerMotif";
import { PublicHeader } from "../../components/Layout/PublicHeader";
import { useDocumentMeta } from "../../lib/useDocumentMeta";

/*
 * Velaigal Privacy Policy, structured as a real editorial document with
 * Fraunces section headings, paper-bg article container, saffron rule,
 * and a divider motif between major groups. Legal text unchanged - only
 * the wrapper is restyled.
 */

interface Section {
  id: string;
  number: string;
  title: string;
  paragraphs: string[];
  /** Optional bullet list rendered after the paragraphs. */
  bullets?: string[];
}

const SECTIONS: Section[] = [
  {
    id: "who-we-are",
    number: "1",
    title: "Who we are",
    paragraphs: [
      "VELAIGAL",
      "Operates from Tamil Nadu, India.",
      "Email: im.vmanoj2002@gmail.com",
      "Phone / WhatsApp: +91 8220 243 513",
    ],
  },
  {
    id: "scope",
    number: "2",
    title: "Scope",
    paragraphs: [
      "This policy covers our website, apps, APIs, and services related to AI-generated hiring tasks, candidate submissions, and automated screening.",
    ],
  },
  {
    id: "data-collected",
    number: "3",
    title: "Data we collect",
    paragraphs: [],
    bullets: [
      "Account data: names, emails, phone (optional), company, role, and login metadata",
      "Recruitment data: job descriptions, resumes/CVs, AI-generated task assignments, candidate task submissions (hosted links, repositories, documents, or videos), AI-generated screening reports and screenshots",
      "Usage and device data: IP, device identifiers, logs, performance metrics, cookie IDs",
      "Support and feedback: tickets, chats, survey responses",
    ],
  },
  {
    id: "how-we-use",
    number: "4",
    title: "How we use data",
    paragraphs: [],
    bullets: [
      "Provide, secure, and improve the Service",
      "Generate hiring tasks from job descriptions; pre-screen candidate submissions and produce advisory reports for recruiters",
      "Detect, prevent, and investigate fraud, abuse, and security incidents",
      "Communicate about service updates and support",
      "Comply with legal obligations",
    ],
  },
  {
    id: "legal-bases",
    number: "5",
    title: "Legal bases (GDPR)",
    paragraphs: [],
    bullets: [
      "Contractual necessity (providing the Service)",
      "Legitimate interests (security, product improvement, analytics)",
      "Consent (where required, for certain cookies, marketing, or candidate processing if relying on consent)",
      "Legal obligations (tax, compliance)",
    ],
  },
  {
    id: "automated-processing",
    number: "6",
    title: "Automated processing and human review",
    paragraphs: [
      "Velaigal's AI pre-screens candidate task submissions and produces advisory scores and flags for the reviewing recruiter. It does not make hiring decisions. A human — the recruiter — reviews every application and makes all shortlist and reject decisions; the AI never auto-rejects a candidate.",
      "Where a candidate submission is a hosted link (for example, a deployed project or repository), an automated browser visits the link in a sandboxed environment and captures screenshots as screening evidence. These screenshots are retained for a limited period — up to 60 days after the screening finishes — and then deleted.",
      "Users can request human review of outcomes affecting them.",
    ],
  },
  {
    id: "sharing",
    number: "7",
    title: "Sharing and disclosure",
    paragraphs: [
      "We share data with the following named subprocessors, solely to run the hiring workflow described in this policy. We do not sell personal data, and we do not share it for advertising purposes.",
    ],
    bullets: [
      "OpenAI — processes job descriptions, resume text, and candidate task submission content to generate hiring tasks and to produce AI pre-screening reports",
      "Backblaze B2 — stores uploaded resumes and submitted task documents",
      "Brevo — sends transactional email (account, application, and notification emails)",
      "Legal authorities, when required by law",
    ],
  },
  {
    id: "international",
    number: "8",
    title: "International transfers",
    paragraphs: [],
    bullets: [
      "Velaigal's own infrastructure runs on a single server (see Security); this section concerns our subprocessors, not Velaigal-operated redundancy",
      "Our named subprocessors (OpenAI, Backblaze B2, Brevo) may process or store data in multiple regions as part of their own infrastructure",
      "Transfers use SCCs, adequacy decisions, or comparable safeguards",
    ],
  },
  {
    id: "security",
    number: "9",
    title: "Security",
    paragraphs: [],
    bullets: [
      "Encrypted transport (TLS)",
      "Role-based access controls, an audit log of administrative actions, and least-privilege access to candidate data",
      "Self-managed PostgreSQL infrastructure with daily backups",
      "We follow established security practices such as encrypted transport, access controls, and least-privilege data access; as an early-stage product we have not yet pursued formal certification such as SOC 2 or ISO 27001",
    ],
  },
  {
    id: "retention",
    number: "10",
    title: "Data retention",
    paragraphs: [],
    bullets: [
      "Screening screenshots are purged 60 days after screening finishes",
      "Resumes and submissions are retained while your application or account is active, and deleted on request",
      "On account closure, data is securely deleted after required retention periods unless law requires longer storage",
    ],
  },
  {
    id: "your-rights",
    number: "11",
    title: "Your rights",
    paragraphs: ["Depending on your region (e.g., GDPR, CCPA), you may:"],
    bullets: [
      "Access, correct, delete, or download your data",
      "Object to or restrict processing; withdraw consent",
      "Not be subject solely to automated decisions with legal or similar significant effects",
      "Requests: im.vmanoj2002@gmail.com (we may verify your identity)",
    ],
  },
  {
    id: "cookies",
    number: "12",
    title: "Cookies and tracking",
    paragraphs: [],
    bullets: [
      "We use only an essential session cookie, for login and security — no analytics or marketing cookies",
      "Manage cookies via your browser settings; there is no in-app cookie banner",
    ],
  },
  {
    id: "children",
    number: "13",
    title: "Children's data",
    paragraphs: [],
    bullets: [
      "Service is not for children under 16 (or lower age permitted by local law)",
      "We do not knowingly collect such data",
    ],
  },
  {
    id: "dpo",
    number: "14",
    title: "Data Protection Officer (DPO) and contact",
    paragraphs: [
      "DPO / Privacy: im.vmanoj2002@gmail.com",
      "Support: im.vmanoj2002@gmail.com",
      "Phone / WhatsApp: +91 8220 243 513",
    ],
  },
  {
    id: "changes",
    number: "15",
    title: "Changes to this policy",
    paragraphs: [],
    bullets: [
      'Updates will be posted here with a revised "Last updated" date',
      "Material changes will be communicated through the app or by email",
    ],
  },
];

const PrivacyPolicy: React.FC = () => {
  useDocumentMeta(
    "Privacy Policy · Velaigal",
    "How Velaigal collects, uses, and protects personal data for HR teams, recruiters, candidates, and visitors."
  );

  return (
    <div className="min-h-screen bg-cream text-ink">
      <PublicHeader variant="slim" />

      <main className="max-w-[720px] mx-auto px-6 md:px-8 py-16 md:py-20">
        <article className="bg-paper border border-hairline rounded-md px-8 py-16 md:px-14 md:py-20">
          {/* Document head */}
          <header>
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Legal · Velaigal
            </span>
            <h1
              className="font-display text-4xl font-medium text-ink leading-[1.05] tracking-tight mt-4"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
            >
              Privacy policy
            </h1>
            <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-6" />
            <p className="mt-6 text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Last updated · 29 August 2026
            </p>
            <p className="mt-8 text-base text-ink-secondary leading-relaxed">
              This Privacy Policy explains how Velaigal processes personal
              data for HR teams, recruiters, candidates, and visitors.
            </p>
          </header>

          <DividerMotif className="mt-12" />

          {/* Numbered sections. Section dividers after clauses 5 and 11 to
           * group the document into intro / processing / rights & contact. */}
          <div className="mt-12 space-y-12">
            {SECTIONS.map((section, idx) => (
              <React.Fragment key={section.id}>
                <section id={section.id} className="scroll-mt-24">
                  <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                    Section {section.number}
                  </span>
                  <h2
                    className="font-display text-xl text-ink leading-snug mt-3"
                    style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
                  >
                    {section.title}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="mt-4 text-base text-ink-secondary leading-relaxed"
                    >
                      {para}
                    </p>
                  ))}
                  {section.bullets && (
                    <ul className="mt-4 space-y-2">
                      {section.bullets.map((item, bIdx) => (
                        <li
                          key={bIdx}
                          className="flex items-start gap-3 text-base text-ink-secondary leading-relaxed"
                        >
                          <span
                            aria-hidden="true"
                            className="mt-3 block w-3 h-px bg-saffron shrink-0"
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                {(idx === 4 || idx === 10) && (
                  <DividerMotif className="py-2" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Editorial signature line */}
          <footer className="mt-20 pt-10 border-t border-hairline">
            <p className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium text-center">
              Velaigal · Tamil Nadu, India
            </p>
          </footer>
        </article>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
